import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { parseISO } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  ArrowLeft, 
  Calendar, 
  User, 
  Mail, 
  Briefcase, 
  Building2, 
  Fingerprint, 
  Clock, 
  CheckCircle2, 
  TrendingUp,
  History,
  AlertTriangle,
  FileText,
  Download,
  ShieldAlert,
  FileCheck
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const StatusBadge = ({ status }) => {
  const statusConfig = {
    pendiente: { label: "Pendiente", className: "bg-amber-100 text-amber-700 border-amber-200" },
    aprobado: { label: "Aprobado", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    rechazado: { label: "Rechazado", className: "bg-red-100 text-red-700 border-red-200" },
    anulado: { label: "Anulado", className: "bg-slate-100 text-slate-700 border-slate-200" },
  };

  const config = statusConfig[status] || statusConfig.pendiente;

  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
};

const TipoBadge = ({ tipo }) => {
  const configs = {
    verbal: { label: "Verbal", className: "bg-blue-100 text-blue-800 border-blue-300" },
    escrita: { label: "Escrita", className: "bg-amber-100 text-amber-800 border-amber-300" },
    "falta grave": { label: "Falta Grave", className: "bg-orange-100 text-orange-800 border-orange-300" },
    suspensión: { label: "Suspensión", className: "bg-rose-100 text-rose-800 border-rose-300" },
    suspension: { label: "Suspensión", className: "bg-rose-100 text-rose-800 border-rose-300" },
  };
  const key = tipo?.toLowerCase() || "escrita";
  const config = configs[key] || { label: tipo, className: "bg-slate-100 text-slate-800 border-slate-300" };

  return (
    <Badge variant="outline" className={`font-semibold px-2.5 py-0.5 text-xs ${config.className}`}>
      {config.label}
    </Badge>
  );
};

const EmployeeDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [llamadas, setLlamadas] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const [empRes, llamadasRes] = await Promise.all([
        api.getEmployeeDetails(id),
        api.getLlamadas(id).catch(() => ({ data: [] }))
      ]);
      setData(empRes.data);
      setLlamadas(llamadasRes.data || []);
    } catch (error) {
      console.error("Error fetching employee details:", error);
      toast.error("Error al cargar los detalles del colaborador");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const handleDownloadActa = async (llamadaId, nombreEmp = "Colaborador") => {
    try {
      const res = await api.downloadActaPdf(llamadaId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Acta_Llamada_${nombreEmp}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Acta oficial descargada en PDF");
    } catch (err) {
      toast.error("Error al descargar el acta en PDF");
    }
  };

  const handleDownloadAdjunto = async (llamadaId, nombre = "Soporte.pdf") => {
    try {
      const res = await api.downloadLlamadaAdjunto(llamadaId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", nombre);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Descarga iniciada");
    } catch (err) {
      toast.error("Error al descargar archivo adjunto");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!data) return null;

  const { empleado, balances, historial } = data;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return parseISO(dateString).toLocaleDateString("es-GT", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn pb-10">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="outline" 
          size="icon" 
          onClick={() => navigate(-1)}
          className="rounded-full border-slate-200 hover:bg-slate-50"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
            Perfil del Colaborador
          </h1>
          <p className="text-slate-500 text-sm">
            Expediente laboral, balance de vacaciones y registro disciplinario
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Basic Info */}
        <div className="space-y-6 lg:col-span-1">
          <Card className="border-slate-100 shadow-sm overflow-hidden">
            <div className="h-24 bg-gradient-to-r from-orange-400 to-orange-600"></div>
            <CardContent className="relative pt-12 -mt-12 text-center">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-white rounded-full p-1 shadow-lg">
                <div className="w-full h-full bg-slate-100 rounded-full flex items-center justify-center">
                  <User className="w-12 h-12 text-slate-400" />
                </div>
              </div>
              
              <h2 className="text-xl font-bold text-slate-800">
                {empleado.nombre} {empleado.apellido}
              </h2>
              <p className="text-slate-500 text-sm font-medium">{empleado.puesto}</p>
              
              <div className="mt-6 space-y-3 text-left">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <Fingerprint className="w-4 h-4 text-orange-500" />
                  <div>
                    <p className="text-xs text-slate-400 uppercase font-bold tracking-wider">DPI</p>
                    <p className="text-sm font-semibold text-slate-700">{empleado.dpi}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <Mail className="w-4 h-4 text-orange-500" />
                  <div>
                    <p className="text-xs text-slate-400 uppercase font-bold tracking-wider">Email</p>
                    <p className="text-sm font-semibold text-slate-700 truncate">{empleado.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <Briefcase className="w-4 h-4 text-orange-500" />
                  <div>
                    <p className="text-xs text-slate-400 uppercase font-bold tracking-wider">Departamento</p>
                    <p className="text-sm font-semibold text-slate-700">{empleado.departamento}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <Building2 className="w-4 h-4 text-orange-500" />
                  <div>
                    <p className="text-xs text-slate-400 uppercase font-bold tracking-wider">Empresa</p>
                    <p className="text-sm font-semibold text-slate-700">{empleado.empresa || "CPS Logistics"}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <User className="w-4 h-4 text-orange-500" />
                  <div>
                    <p className="text-xs text-slate-400 uppercase font-bold tracking-wider">Supervisor</p>
                    <p className="text-sm font-semibold text-slate-700">{empleado.supervisor_nombre || "N/A"}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <Calendar className="w-4 h-4 text-orange-500" />
                  <div>
                    <p className="text-xs text-slate-400 uppercase font-bold tracking-wider">Fecha de Ingreso</p>
                    <p className="text-sm font-semibold text-slate-700">{formatDate(empleado.fecha_ingreso)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Tabs (Vacaciones & Balances vs. Llamadas de Atención) */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="vacaciones" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-slate-100 p-1 rounded-xl">
              <TabsTrigger value="vacaciones" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                <Calendar className="w-4 h-4 mr-2 text-orange-500" />
                Vacaciones & Saldo
              </TabsTrigger>
              <TabsTrigger value="llamadas" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                <AlertTriangle className="w-4 h-4 mr-2 text-amber-500" />
                Llamadas de Atención ({llamadas.length})
              </TabsTrigger>
            </TabsList>

            {/* TAB: VACACIONES */}
            <TabsContent value="vacaciones" className="space-y-6 mt-4">
              {/* Balance Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-emerald-100 bg-emerald-50/30">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-emerald-600 uppercase tracking-wider">Días Disponibles</p>
                        <h3 className="text-3xl font-bold text-emerald-700">{balances?.disponibles ?? 0}</h3>
                      </div>
                      <div className="p-3 bg-emerald-100 text-emerald-600 rounded-xl">
                        <CheckCircle2 className="w-6 h-6" />
                      </div>
                    </div>
                    <p className="text-xs text-emerald-600 mt-4">Saldo actual para gozar</p>
                  </CardContent>
                </Card>

                <Card className="border-sky-100 bg-sky-50/30">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-sky-600 uppercase tracking-wider">Días Usados</p>
                        <h3 className="text-3xl font-bold text-sky-700">{balances?.total_usado_historico ?? 0}</h3>
                      </div>
                      <div className="p-3 bg-sky-100 text-sky-600 rounded-xl">
                        <TrendingUp className="w-6 h-6" />
                      </div>
                    </div>
                    <p className="text-xs text-sky-600 mt-4">Total de días tomados históricamente</p>
                  </CardContent>
                </Card>

                <Card className="border-orange-100 bg-orange-50/30">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-orange-600 uppercase tracking-wider">Acumulado Total</p>
                        <h3 className="text-3xl font-bold text-orange-700">{balances?.total_acumulado_historico ?? 0}</h3>
                      </div>
                      <div className="p-3 bg-orange-100 text-orange-600 rounded-xl">
                        <Calendar className="w-6 h-6" />
                      </div>
                    </div>
                    <p className="text-xs text-orange-600 mt-4">Días generados desde ingreso</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 bg-slate-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Base Plataforma</p>
                        <h3 className="text-3xl font-bold text-slate-700">{balances?.acumulado_ley ?? 0}</h3>
                      </div>
                      <div className="p-3 bg-slate-200 text-slate-500 rounded-xl">
                        <History className="w-6 h-6" />
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-4 underline decoration-slate-300">Días ley + iniciales (baseline)</p>
                  </CardContent>
                </Card>
              </div>

              {/* History Table */}
              <Card className="border-slate-100 shadow-sm">
                <CardHeader className="bg-slate-50/50 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-slate-400" />
                    <CardTitle className="text-lg">Historial de Solicitudes</CardTitle>
                  </div>
                  <CardDescription>Registro de todas las vacaciones solicitadas</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Periodo</TableHead>
                        <TableHead>Días</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Fecha Solicitud</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historial.length > 0 ? (
                        historial.map((req) => (
                          <TableRow key={req.id}>
                            <TableCell>
                              <div className="font-medium text-slate-700">
                                {formatDate(req.fecha_inicio)}
                              </div>
                              <div className="text-xs text-slate-400">
                                al {formatDate(req.fecha_fin)}
                              </div>
                            </TableCell>
                            <TableCell className="font-semibold text-slate-800">
                              {req.dias_solicitados}
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={req.estado} />
                            </TableCell>
                            <TableCell className="text-xs text-slate-500">
                              {formatDate(req.fecha_creacion)}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} className="h-32 text-center text-slate-400">
                            No hay historial de solicitudes para este colaborador.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB: LLAMADAS DE ATENCION */}
            <TabsContent value="llamadas" className="space-y-6 mt-4">
              {/* Resumen de llamadas */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-xs text-amber-700 font-medium">Total Llamadas</p>
                  <p className="text-2xl font-bold text-amber-900 mt-0.5">{llamadas.length}</p>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-xs text-blue-700 font-medium">Verbales / Escritas</p>
                  <p className="text-2xl font-bold text-blue-900 mt-0.5">
                    {llamadas.filter((l) => ["verbal", "escrita"].includes(l.tipo_llamada?.toLowerCase())).length}
                  </p>
                </div>
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl">
                  <p className="text-xs text-rose-700 font-medium">Graves / Suspensión</p>
                  <p className="text-2xl font-bold text-rose-900 mt-0.5">
                    {llamadas.filter((l) => l.tipo_llamada?.toLowerCase().includes("grave") || l.tipo_llamada?.toLowerCase().includes("suspens")).length}
                  </p>
                </div>
              </div>

              {/* Lista de Llamadas */}
              <Card className="border-slate-100 shadow-sm">
                <CardHeader className="bg-slate-50/50 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="w-5 h-5 text-amber-500" />
                      <CardTitle className="text-lg">Expediente Disciplinario</CardTitle>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {llamadas.length} registro(s)
                    </Badge>
                  </div>
                  <CardDescription>
                    Actas administrativas de retroalimentación disciplinaria formal emitidas
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  {llamadas.length === 0 ? (
                    <div className="py-12 text-center">
                      <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3">
                        <CheckCircle2 className="w-6 h-6" />
                      </div>
                      <h4 className="font-semibold text-slate-800">Expediente Impecable</h4>
                      <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
                        Este colaborador no cuenta con llamadas de atención ni faltas disciplinarias registradas.
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {llamadas.map((l) => (
                        <div key={l.id} className="p-4 hover:bg-slate-50/60 transition-colors">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                            <div className="space-y-1.5 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <TipoBadge tipo={l.tipo_llamada} />
                                <span className="font-semibold text-slate-800 text-sm">{l.motivo}</span>
                                <span className="text-xs text-slate-400">• Incidente: {l.fecha_incidente}</span>
                              </div>
                              <p className="text-xs text-slate-600 leading-relaxed bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                                {l.descripcion}
                              </p>
                              {l.plan_accion && (
                                <p className="text-xs text-emerald-700 font-medium">
                                  <strong>Compromiso:</strong> {l.plan_accion}
                                </p>
                              )}
                              <p className="text-[11px] text-slate-400">
                                Registrado por: <strong className="text-slate-600">{l.supervisor_nombre}</strong> el {l.fecha_registro}
                              </p>
                            </div>

                            <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadActa(l.id, `${empleado.nombre}_${empleado.apellido}`)}
                                className="border-amber-200 text-amber-800 hover:bg-amber-50 text-xs shadow-sm flex items-center gap-1.5"
                              >
                                <FileText className="w-3.5 h-3.5 text-amber-600" />
                                Acta Oficial PDF
                              </Button>

                              {l.tiene_adjunto && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDownloadAdjunto(l.id, `Soporte_${l.id}.pdf`)}
                                  className="text-xs text-slate-600 hover:bg-slate-100"
                                >
                                  <Download className="w-3.5 h-3.5 mr-1" />
                                  Evidencia
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDetailPage;
